import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { Grid, Box } from '@mui/material';
import { gql } from '@apollo/client';
import Typography from '@atoms/Typography';
import Skeleton from '@atoms/Skeleton';
import Avatar from '@atoms/Avatar';
import Spacer from '@atoms/Spacer';
import Card from '@app/components/atoms/Card';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import StyleIcon from '@mui/icons-material/Style';
import MessagesList from '@molecules/opportunity-dashboard/MessagesList';

import { APPLICATIONS_STATUS } from '@app/config/constants';
import { getCurrencySymbol } from '@app/helpers';
import { useTranslation } from '@app/config/i18n';

import OpportunityCard from '../../opportunities/OpportunityCard';

import {
  TabContainer,
  Header,
  ApplicationName,
  CardMedia,
  BlackShade,
  OpportunityStyledBox
} from './style';

const ApplicationSidebar = ({ applicationDetails }) => {
  const { t } = useTranslation(['applications', 'dashboard', 'common']);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardMedia image={applicationDetails.opportunity.logo.path}>
            <BlackShade>
              {applicationDetails.opportunity.start_date && (
                <Typography variant="overline" gutterBottom>
                  {moment(applicationDetails.opportunity.start_date).format('DD MMM')}
                  {applicationDetails.opportunity.end_date &&
                    ` - ${moment(applicationDetails.opportunity.end_date).format('DD MMM ‘YY')}`}
                </Typography>
              )}{' '}
              <Typography variant="h5">{applicationDetails.opportunity.title}</Typography>
            </BlackShade>
          </CardMedia>
          <Box p={3}>
            {applicationDetails.opportunity.organisation && (
              <OpportunityStyledBox>
                <BusinessRoundedIcon fontSize="small" />
                <Typography variant="caption">
                  {applicationDetails.opportunity.organisation.name}
                </Typography>
              </OpportunityStyledBox>
            )}
            <OpportunityStyledBox>
              <StyleIcon fontSize="small" />
              <Typography variant="caption">{applicationDetails.opportunity.event_type.name}</Typography>
            </OpportunityStyledBox>
            {applicationDetails.opportunity.location && (
              <OpportunityStyledBox>
                <LocationOnRoundedIcon fontSize="small" />
                <Typography variant="caption">
                  {applicationDetails.opportunity.location.formatted_address}
                </Typography>
              </OpportunityStyledBox>
            )}
          </Box>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <TabContainer padding="1rem">
          <Header container>
            <Grid direction="row" item container>
              <ApplicationName>
                {applicationDetails.status === APPLICATIONS_STATUS.WAITING && (
                  <Typography variant="body1" weight="600">
                    {t('status_message.waiting')}
                  </Typography>
                )}
                {applicationDetails.status === APPLICATIONS_STATUS.APPROVED && (
                  <Typography variant="body1" weight="600">
                    {t('status_message.approved')}
                  </Typography>
                )}
                {applicationDetails.status === APPLICATIONS_STATUS.REJECTED && (
                  <Typography variant="body1" weight="600">
                    {t('status_message.declinded')}
                  </Typography>
                )}
              </ApplicationName>
            </Grid>
          </Header>
        </TabContainer>
      </Grid>
      {applicationDetails.payment_info && (
        <Grid item xs={12}>
          <TabContainer padding="1rem">
            <Typography variant="h5">{t('payment:payment_information')}</Typography>
            <Spacer height="1rem" />
            {applicationDetails.payment_info.is_external ? (
              <Typography variant="body1">
                <span>{t('payment:external_payment')}</span>
              </Typography>
            ) : (
              <Typography variant="body1">
                <span>{t('payment:paid')}</span> {t('common:yes')}
              </Typography>
            )}

            <Typography variant="body1">
              <span> {t('payment:amount')} </span> {applicationDetails.payment_info.amount}{' '}
              {getCurrencySymbol(applicationDetails.payment_info.currency)}
            </Typography>
            {applicationDetails.payment_info.coupon && (
              <>
                <Typography variant="body1">
                  <span>{t('payment:with_coupon')} </span>:{' '}
                  {applicationDetails.payment_info.coupon}{' '}
                </Typography>
                <Typography variant="body1">
                  <span>{t('payment:discount_percentage')}</span>:{' '}
                  {applicationDetails.payment_info.discount_percent}%
                </Typography>
              </>
            )}
            {applicationDetails.payment_info.is_external ? (
              <Typography variant="body1">
                <span>{t('payment:payment_confirmed_at')} </span>
                {moment(applicationDetails.payment_info.created_at).format('YYYY-MM-DD')}
              </Typography>
            ) : (
              <Typography variant="body1">
                <span>{t('payment:paid_at')}</span>{' '}
                {moment(applicationDetails.payment_info.created_at).format('YYYY-MM-DD')}
              </Typography>
            )}
          </TabContainer>
        </Grid>
      )}
      <Grid item xs={12}>
        <TabContainer padding="1rem">
          <Grid container direction="row" alignItems="center">
            <Grid item xs={3}>
              <Avatar
                width="100px"
                src={applicationDetails.opportunity.organisation && applicationDetails.opportunity.organisation.logo && applicationDetails.opportunity.organisation.logo.thumb}
              />
            </Grid>
            <Grid item xs={9}>
              <ApplicationName>
                <Typography variant="h4">
                  {applicationDetails.opportunity.organisation && applicationDetails.opportunity.organisation.name}
                </Typography>

                {/* <ApplicationMeta>
              <RoomIcon /> Brussels, Belgium
            </ApplicationMeta> */}
              </ApplicationName>
            </Grid>
          </Grid>
        </TabContainer>
      </Grid>
      <Grid item xs={12}>
        <TabContainer padding="1.5rem">
          <MessagesList application={applicationDetails} />
        </TabContainer>
      </Grid>
    </Grid>
  );
};

ApplicationSidebar.propTypes = {
  applicationDetails: PropTypes.object.isRequired,
};


ApplicationSidebar.Skeleton = () => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <OpportunityCard
        loading
      />
    </Grid>
    <Grid item xs={12}>
      <TabContainer padding="1rem">
        <Header container>
          <Grid direction="row" item container>
            <ApplicationName>
              <Skeleton animation="wave" height={50} margin="7px 0" />
              <Skeleton animation="wave" height={50} margin="7px 0" />
              <Skeleton animation="wave" height={50} margin="7px 0" />
            </ApplicationName>
          </Grid>
        </Header>
      </TabContainer>
    </Grid>
    <Grid item xs={12}>
      <TabContainer padding="1rem">
        <Grid container direction="row" alignItems="center">
          <Grid item xs={3}>
            <Skeleton animation="wave" variant="circle" width={64} height={64} />
          </Grid>
          <Grid item xs={9}>
            <ApplicationName>
              <Skeleton animation="wave" height={40} margin="0 12px" />
            </ApplicationName>
          </Grid>
        </Grid>
      </TabContainer>
    </Grid>
    <Grid item xs={12}>
      <TabContainer padding="1.5rem">
        <Skeleton animation="wave" height={200} />
      </TabContainer>
    </Grid>
  </Grid>
)
ApplicationSidebar.fragments = {
  application: gql`
    fragment ApplicantSidebar on ApplicationSubmission {
      id
      status
      opportunity {
        ...OpportunityCard
        organisation {
          name
          logo {
            thumb
          }
        }
      }
    }
    ${OpportunityCard.fragments.opportunity}
  `,
};

export default ApplicationSidebar;
